import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Check, Database, ExternalLink, FileCheck, FileText, Layers, RefreshCw, Search, Trash2, TriangleAlert, Upload, X, Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { useSourceViewer } from '@/app/source-viewer'
import { PageHeader } from '@/app/shell/PageHeader'
import {
  qk, useDeleteFile, useDeleteImpact, useFiles, useMappings, useProcessFile, useQuestions, useReviewMapping, useUploadFile,
} from '@/lib/api/hooks'
import { tok } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { EvidenceCitation } from '@/ui/evidence-citation'
import { PackBadge } from '@/ui/pack-badge'
import { EmptyState, LoadingBlock } from '@/ui/states'
import { cn } from '@/lib/utils'

const shortId = (qid: string) => qid.replace(/^BESS-/, '')
const ACCEPT = '.pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg'

const RESP_TONE: Record<string, string> = {
  compliant: tok('--stable'), partially_effective: tok('--ember'), degraded: tok('--clear'),
  unverified: tok('--trace'), temporary_override: tok('--ember'), impaired: tok('--flare'),
  unknown: tok('--faint'), absent: tok('--ignition'),
}

// Pipeline nodes 0..5 — index maps to File.processing_stage (last completed node).
const STAGES: { label: string; Icon: LucideIcon }[] = [
  { label: 'Uploaded', Icon: Upload }, { label: 'Extracted', Icon: FileText },
  { label: 'Chunked', Icon: Layers }, { label: 'Embedded', Icon: Database },
  { label: 'Matched', Icon: Search }, { label: 'Mapped', Icon: FileCheck },
]

/** Stage stepper driven by real polled status: stage = last completed node. */
function Pipeline({ stage, status }: { stage: number; status: string }) {
  const failed = status === 'failed'
  const done = status === 'mapped'
  const activeIdx = done ? 99 : stage + 1 // node currently being worked / that failed
  return (
    <div className="mt-3.5 flex items-center">
      {STAGES.map((s, i) => {
        const isDone = i <= stage
        const isActive = !failed && !done && i === activeIdx
        const isError = failed && i === activeIdx
        const color = isError ? tok('--ignition') : isDone ? tok('--stable') : isActive ? tok('--ember') : tok('--faint')
        const filled = isDone || isError || isActive
        return (
          <Fragment key={s.label}>
            {i > 0 && <div className="h-0.5 flex-1" style={{ background: i <= stage ? tok('--stable') : tok('--input') }} />}
            <div className="flex w-[52px] shrink-0 flex-col items-center gap-1.5">
              <span
                className="grid size-6 place-items-center rounded-full border"
                style={{ borderColor: color, color: filled ? '#fff' : color, background: isDone ? tok('--stable') : isError ? tok('--ignition') : isActive ? tok('--ember') : 'transparent' }}
              >
                {isDone ? <Check className="size-3" /> : isError ? <X className="size-3" /> : isActive ? <RefreshCw className="size-3 animate-spin" /> : <s.Icon className="size-3" />}
              </span>
              <span className="text-[8.5px] font-semibold uppercase tracking-wide" style={{ color }}>{s.label}</span>
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}

export function EvidencePage() {
  const { site, siteId } = useCurrentSite()
  const id = siteId ?? ''
  const { role } = useRole()
  const canWrite = role === 'assessor'
  const { open: openSource } = useSourceViewer()
  const files = useFiles(id)
  const mappings = useMappings(id)
  const questions = useQuestions()
  const upload = useUploadFile(id)
  const retry = useProcessFile(id)
  const review = useReviewMapping(id)
  const del = useDeleteFile(id)
  const fileInput = useRef<HTMLInputElement>(null)
  const [drag, setDrag] = useState(false)
  const [delId, setDelId] = useState<string | null>(null)

  const qText = new Map((questions.data ?? []).map((q) => [q.question_id, q.question_text]))
  const qc = useQueryClient()

  // One review card per QUESTION (best-confidence mapping) — a question can be matched
  // from several chunks; "to review" is counted in questions, matching the Assessment.
  const reviewList = useMemo(() => {
    const arr = mappings.data ?? []
    const byQ = new Map<string, (typeof arr)[number]>()
    for (const m of arr) {
      const cur = byQ.get(m.question_id)
      if (!cur || Number(m.confidence ?? 0) > Number(cur.confidence ?? 0)) byQ.set(m.question_id, m)
    }
    return [...byQ.values()]
  }, [mappings.data])

  // Files poll while processing; mappings don't — so refetch mappings as suggestions
  // stream in / when a file finishes, keeping the review queue live rather than stale.
  const totalMapped = (files.data ?? []).reduce((a, f) => a + f.mapped_count, 0)
  useEffect(() => {
    if (id) qc.invalidateQueries({ queryKey: qk.mappings(id) })
  }, [totalMapped, id, qc])

  const send = (f?: File | null) => f && upload.mutate(f)
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    send(e.target.files?.[0]); e.target.value = ''
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false)
    if (canWrite) send(e.dataTransfer.files?.[0])
  }

  return (
    <>
      <PageHeader
        title="Evidence"
        subtitle={
          <>
            {site && <PackBadge pack={site.pack} />}
            <span>{files.data?.length ?? 0} documents</span>
            <span className="text-faint">·</span>
            <span>{reviewList.length} to review</span>
          </>
        }
        actions={canWrite && (
          <Button size="sm" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
            <Upload /> {upload.isPending ? 'Uploading…' : 'Upload documents'}
          </Button>
        )}
      />
      <input ref={fileInput} type="file" hidden accept={ACCEPT} onChange={onPick} />

      {site?.pack === 'DSEAR' && (
        <div className="mb-4 rounded-md border border-flare/40 bg-flare/[0.07] px-3 py-2 text-[12.5px] text-muted-foreground">
          Petrochem engine metadata is in progress — evidence still processes and maps, but confirmed answers
          won&rsquo;t recompute the underwriting indices yet.
        </div>
      )}

      {canWrite && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => fileInput.current?.click()}
          className={cn(
            'mb-4 cursor-pointer rounded-xl border border-dashed p-5 text-center transition-colors',
            drag ? 'border-primary bg-primary/[0.04]' : 'border-input hover:border-primary/60',
          )}
        >
          <div className="mx-auto grid size-9 place-items-center rounded-full bg-primary/10 text-primary">
            <Upload className="size-[18px]" />
          </div>
          <div className="mt-2 font-display text-[14px] font-semibold">
            Drop a document to ingest, or <span className="text-primary">browse</span>
          </div>
          <div className="mt-1 text-[12px] text-muted-foreground">
            PDF, DOCX, XLSX, images · each upload auto-extracts, embeds, classifies and maps to questions
          </div>
          {upload.isError && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-ignition">
              <TriangleAlert className="size-3.5" /> Upload failed — check the file and try again.
            </div>
          )}
          {upload.isSuccess && !upload.isPending && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-stable">
              <Check className="size-3.5" /> Uploaded — processing below.
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_1fr]">
        {/* ingestion pipeline */}
        <div>
          <KLabel className="mb-2 flex items-center gap-1.5">
            <Database className="size-3.5" /> Ingestion pipeline
          </KLabel>
          {files.isLoading ? (
            <Card><LoadingBlock lines={4} /></Card>
          ) : (files.data?.length ?? 0) === 0 ? (
            <EmptyState kind="no-data" title="No documents yet">
              Upload a fire-safety report, HAZOP or maintenance log — it processes automatically.
            </EmptyState>
          ) : (
            <div className="space-y-3">
              {files.data!.map((f) => {
                const retrying = retry.isPending && retry.variables === f.file_id
                const status = retrying ? 'queued' : f.processing_status
                const failed = status === 'failed'
                const mapped = status === 'mapped'
                return (
                  <Card key={f.file_id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <FileText className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-semibold">{f.file_name}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            {f.extraction_quality != null && !failed && (
                              <span className="text-[11px] text-faint">quality {Math.round(Number(f.extraction_quality) * 100)}%</span>
                            )}
                            {(f.module_tags ?? []).slice(0, 3).map((t) => (
                              <span key={t.module_id} className="rounded bg-surface-2 px-1.5 py-px text-[10px] text-muted-foreground">{t.module_id}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {mapped ? (
                          <RiskChip label="mapped" tone={tok('--stable')} />
                        ) : failed ? (
                          <RiskChip label="failed" tone={tok('--ignition')} />
                        ) : (
                          <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-surface-2 px-2 py-1 text-[11px] text-muted-foreground">
                            <RefreshCw className="size-3 animate-spin" /> {status}
                          </span>
                        )}
                        {canWrite && (
                          <button onClick={() => setDelId(f.file_id)} title="Delete document"
                            className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-ignition">
                            <Trash2 className="size-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    <Pipeline stage={retrying ? 0 : f.processing_stage} status={status} />

                    {status === 'retrieving' || status === 'mapping' ? (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[11.5px]">
                          <span className="text-muted-foreground">
                            {status === 'retrieving'
                              ? 'Identifying candidate questions…'
                              : `Judging evidence · ${f.map_done}/${f.map_total} candidate questions`}
                          </span>
                          {f.mapped_count > 0 && <span className="font-medium text-ember">{f.mapped_count} mapped</span>}
                        </div>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-surface-2">
                          <div className="h-full rounded bg-primary transition-all"
                            style={{ width: `${f.map_total ? (f.map_done / f.map_total) * 100 : (status === 'retrieving' ? 8 : 4)}%` }} />
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-[11.5px] text-faint">
                          {failed ? (f.processing_error ?? 'processing failed')
                            : mapped ? (
                              <>
                                {f.chunk_count} chunks
                                {f.mapped_count > 0 && <> · <span className="font-medium text-ember">{f.mapped_count} to review</span></>}
                              </>
                            ) : 'Auto-processing — no action needed'}
                        </span>
                        {failed && canWrite && (
                          <Button size="sm" variant="secondary" onClick={() => retry.mutate(f.file_id)} disabled={retrying}>
                            <RefreshCw className={cn('size-3.5', retrying && 'animate-spin')} /> Retry
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                )
              })}
            </div>
          )}
        </div>

        {/* AI mapping review */}
        <div>
          <KLabel className="mb-2 flex items-center gap-1.5">
            <Zap className="size-3.5 text-primary" /> AI mapping review
            {reviewList.length > 0 && <span className="text-ember">· {reviewList.length} pending</span>}
          </KLabel>
          {mappings.isLoading ? (
            <Card><LoadingBlock lines={4} /></Card>
          ) : reviewList.length === 0 ? (
            <EmptyState kind="no-data" title="Nothing to review">
              As documents finish processing, AI-mapped suggestions with verified quotes appear here to
              accept or decline. Confirmed answers flow straight to the assessment.
            </EmptyState>
          ) : (
            <div className="space-y-3">
              {reviewList.map((m) => {
                const busy = review.isPending && review.variables?.mappingId === m.mapping_id
                return (
                  <Card key={m.mapping_id}>
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="tabular font-display text-[11px] font-semibold text-faint">{shortId(m.question_id)}</div>
                        <div className="mt-0.5 line-clamp-2 text-[13px]">{qText.get(m.question_id) ?? m.question_id}</div>
                      </div>
                      {m.suggested_response && <RiskChip label={m.suggested_response} tone={RESP_TONE[m.suggested_response] ?? tok('--muted-foreground')} />}
                    </div>
                    <EvidenceCitation
                      quote={m.evidence_quote ?? '(no quote)'}
                      source={`match confidence ${Math.round(Number(m.confidence ?? 0) * 100)}%`}
                      verified={m.quote_verified ?? false}
                    />
                    <div className="mt-3 flex items-center gap-2">
                      {canWrite ? (
                        <>
                          <Button size="sm" onClick={() => review.mutate({ mappingId: m.mapping_id, action: 'confirm' })} disabled={busy}>
                            <Check /> Confirm &amp; auto-answer
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => review.mutate({ mappingId: m.mapping_id, action: 'reject' })} disabled={busy}>
                            <X /> Reject
                          </Button>
                        </>
                      ) : (
                        <span className="text-[11.5px] text-faint">Review is assessor-only.</span>
                      )}
                      {m.file_id && (
                        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => openSource(m.file_id!, m.mapping_id)}>
                          <ExternalLink /> Source
                        </Button>
                      )}
                    </div>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {delId && (
        <DeleteEvidenceDialog
          fileId={delId}
          fileName={files.data?.find((f) => f.file_id === delId)?.file_name ?? 'this document'}
          pending={del.isPending}
          onCancel={() => setDelId(null)}
          onConfirm={() => del.mutate(delId, { onSuccess: () => setDelId(null) })}
        />
      )}
    </>
  )
}

function DeleteEvidenceDialog({ fileId, fileName, pending, onCancel, onConfirm }: {
  fileId: string; fileName: string; pending: boolean; onCancel: () => void; onConfirm: () => void
}) {
  const impact = useDeleteImpact(fileId)
  const d = impact.data
  const supported = d ? d.reverting.length + d.kept_supported.length + d.kept_overridden.length : 0
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-xl border bg-popover p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-[17px] font-semibold">Delete {fileName}?</h2>
        {impact.isLoading || !d ? (
          <p className="mt-3 text-[13px] text-muted-foreground">Checking impact…</p>
        ) : d.in_use ? (
          <div className="mt-3 space-y-2 text-[13px]">
            <p className="text-muted-foreground">This document supports <b className="text-foreground">{supported}</b> confirmed answer{supported === 1 ? '' : 's'}.</p>
            {d.reverting.length > 0 && (
              <div className="rounded-md border border-flare/40 bg-flare/[0.08] px-3 py-2 text-muted-foreground">
                <b className="text-foreground">{d.reverting.length}</b> sole-supported answer{d.reverting.length === 1 ? '' : 's'} will revert to <b className="text-foreground">open</b> and the site will recompute — answered{' '}
                <b className="tabular text-foreground">{d.answered_before}</b> → <b className="tabular text-foreground">{d.answered_after}</b>.
              </div>
            )}
            {d.kept_supported.length > 0 && <p className="text-muted-foreground">{d.kept_supported.length} kept — also supported by another document.</p>}
            {d.kept_overridden.length > 0 && <p className="text-muted-foreground">{d.kept_overridden.length} kept — you overrode the suggestion.</p>}
            <p className="text-[12px] text-faint">The document is archived for audit, not erased.</p>
          </div>
        ) : (
          <p className="mt-3 text-[13px] text-muted-foreground">This document hasn&rsquo;t been used in any answer — deleting has no impact on the assessment.</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="destructive" size="sm" disabled={pending} onClick={onConfirm}>
            <Trash2 /> {pending ? 'Deleting…' : 'Delete document'}
          </Button>
        </div>
      </div>
    </div>
  )
}
